import { editedSize, isEdited } from "./imageEdit";

// Turns an edit (imageEdit.js) into pixels, once, when the files are sent.
// Decoding goes through createImageBitmap, which works off the main thread
// and follows the photo's EXIF orientation the same way the preview <img>
// does, so what was on screen is what gets saved. Encoding is async too.

const JPEG_QUALITY = 0.92;

const decode = async file => {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch (error) {
      // Older Safari: no options argument.
      return createImageBitmap(file);
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
};

const makeCanvas = (width, height) => {
  if (typeof OffscreenCanvas === "function") {
    return new OffscreenCanvas(width, height);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const encode = (canvas, type) =>
  typeof canvas.convertToBlob === "function"
    ? canvas.convertToBlob({ type, quality: JPEG_QUALITY })
    : new Promise((resolve, reject) =>
        canvas.toBlob(
          blob => (blob ? resolve(blob) : reject(new Error("encode failed"))),
          type,
          JPEG_QUALITY
        )
      );

/**
 * @param {File} file - a JPEG or PNG
 * @param {{rotation: number, mirror: boolean}} edit
 * @returns {Promise<File>} the file itself when there is nothing to change,
 *   otherwise a new file of the same type and name with the edit applied
 */
export async function renderEditedImage(file, edit) {
  if (!isEdited(edit)) return file;
  const source = await decode(file);
  const width = source.width || source.naturalWidth;
  const height = source.height || source.naturalHeight;
  const size = editedSize(width, height, edit);
  const canvas = makeCanvas(size.width, size.height);
  try {
    const ctx = canvas.getContext("2d");
    ctx.translate(size.width / 2, size.height / 2);
    ctx.rotate((edit.rotation * Math.PI) / 180);
    ctx.scale(edit.mirror ? -1 : 1, 1);
    ctx.drawImage(source, -width / 2, -height / 2);
    const type = file.type === "image/png" ? "image/png" : "image/jpeg";
    const blob = await encode(canvas, type);
    return new File([blob], file.name, { type, lastModified: file.lastModified });
  } finally {
    if (typeof source.close === "function") source.close();
    // A full-size canvas holds tens of MB until collected; let it go now.
    canvas.width = 0;
    canvas.height = 0;
  }
}
